import useAdminToast from '../useAdminToast';
import AdminPagination from '../AdminPagination';
import AdminState from '../AdminState';
import { ChevronDownIcon, RepeatIcon, ChevronUpIcon, DeleteIcon } from '../AdminIcon';
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import { AdminToolbar } from '../AdminLayout/AdminLayout';
import Backend from "../../../utils/api.js";
import { Box, Text, VStack, HStack, Badge, Button, IconButton, Select, Collapse, Code } from '@chakra-ui/react';

import "./FailedSwipesTab.css";

const STAGE_COLORS = {
  frontend_validation: "gray",
  user_lookup: "orange",
  user_not_registered: "yellow",
  session_create: "red",
};

const STAGE_LABELS = {
  frontend_validation: "Validation",
  user_lookup: "User Lookup",
  user_not_registered: "Not Registered",
  session_create: "Session Create",
};

function FailedSwipesTab({ user, labId, pushTab, revert }) {
  const toast = useAdminToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [clearError, setClearError] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stageFilter, setStageFilter] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    loadEntries();
  }, [labId, page, stageFilter]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(loadEntries, 30000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, labId, page, stageFilter]);

  const loadEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Backend.adminGetFailedSwipes(user.UID, labId, page, 50, stageFilter || null);
      if (response.status === 200) {
        setHasLoaded(true);
        setEntries(response.data || []);
        setTotalPages(Math.ceil((response.total || 0) / 50) || 1);
        setTotal(response.total || 0);
      } else if (response.status === 401 || response.status === 403) {
        setError("You do not have permission to view failed check-ins");
      } else {
        setError("Failed to load failed check-ins");
      }
    } catch (err) {
      console.error("Error loading failed check-ins:", err);
      setError("Failed to connect to the server");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    setClearError(false);
    try {
      const response = await Backend.adminClearFailedSwipes(user.UID, labId);
      if (response.status < 200 || response.status >= 300) throw new Error('clear_failed');
      toast({ title: 'Failed check-in cache cleared' });
      setEntries([]);
      setTotal(0);
      setTotalPages(1);
      setPage(1);
      setExpandedRows(new Set());
    } catch {
      setClearError(true);
    } finally {
      setClearing(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <Slider
      name="Failed check-ins"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className={`FailedSwipesTab${loading && hasLoaded ? ' admin-refreshing' : ''}`} aria-busy={loading}>
        <AdminToolbar title={`${labId} - Failed check-ins`} count={<Badge colorScheme="red">{total} cached</Badge>}>
          <Select icon={<ChevronDownIcon />}
            size="md"
            aria-label="Failure stage"
            placeholder="All stages"
            value={stageFilter}
            onChange={(e) => {
              setStageFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="frontend_validation">Validation</option>
            <option value="user_lookup">User Lookup</option>
            <option value="user_not_registered">Not Registered</option>
            <option value="session_create">Session Create</option>
          </Select>
          <Button
            size="md"
            variant={autoRefresh ? "solid" : "outline"}
            colorScheme={autoRefresh ? "green" : "gray"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            aria-pressed={autoRefresh}
          >
            Auto-refresh
          </Button>
          <IconButton
            icon={<RepeatIcon />}
            onClick={loadEntries}
            aria-label="Refresh"
            isLoading={loading}
            size="md"
          />
          <IconButton
            icon={<DeleteIcon />}
            onClick={handleClear}
            aria-label="Clear cache"
            size="md"
            colorScheme="red"
            variant="solid"
            isDisabled={total === 0 || loading}
            isLoading={clearing}
          />
        </AdminToolbar>

        {clearError && <AdminState status="error" title="Failed check-ins could not be cleared"
          description="The cached records are still available. Use Clear cache to try again." />}

        {/* Error Display */}
        {error && (
          <AdminState status="error" title="Failed check-ins could not be loaded" description={error} onRetry={loadEntries} />
        )}

        {/* Loading State */}
        {loading && !hasLoaded ? (
          <AdminState status="loading" title="Loading failed check-ins…" />
        ) : error ? null : entries.length === 0 ? (
          <AdminState title="No failed check-ins" description="There are no failed check-ins matching these filters in the last 7 days." />
        ) : (
          <>
            <VStack align="stretch" spacing={2} className="failed-swipes-list">
              {entries.map((entry, index) => (
                <Box key={entry.id || index} className="failed-swipe-card">
                  <HStack
                    justify="space-between"
                    className="admin-record-heading"
                    cursor="pointer"
                    onClick={() => toggleRow(index)}
                  >
                    <VStack align="start" spacing={1} minW={0} flex={1}>
                      <HStack spacing={0} gap={2} flexWrap="wrap">
                        <Badge colorScheme={STAGE_COLORS[entry.failure_stage] || "gray"}>
                          {STAGE_LABELS[entry.failure_stage] || entry.failure_stage}
                        </Badge>
                        <Text fontSize="sm" color="gray.600">
                          {formatDateTime(entry.timestamp)}
                        </Text>
                        {entry.http_status && (
                          <Badge colorScheme={entry.http_status >= 500 ? "red" : "orange"}>
                            HTTP {entry.http_status}
                          </Badge>
                        )}
                      </HStack>
                      <HStack spacing={0} gap={2} flexWrap="wrap" fontSize="sm">
                        <Text fontWeight="medium">
                          Card: {entry.swipe_number || entry.swipe_number_masked}
                        </Text>
                        <Text color="gray.600" maxW="400px" isTruncated>
                          {entry.failure_reason}
                        </Text>
                      </HStack>
                    </VStack>
                    <IconButton
                      icon={expandedRows.has(index) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                      aria-label={expandedRows.has(index) ? "Collapse failure details" : "Expand failure details"}
                      aria-expanded={expandedRows.has(index)}
                      onClick={event => { event.stopPropagation(); toggleRow(index); }}
                    />
                  </HStack>

                  <Collapse in={expandedRows.has(index)} animateOpacity>
                    <Box mt={3} pt={3} borderTop="1px solid #e2e8f0">
                      <VStack align="stretch" spacing={2} fontSize="sm">
                        <HStack className="admin-detail-row">
                          <Text fontWeight="semibold" minW="160px">Failure Reason:</Text>
                          <Text>{entry.failure_reason}</Text>
                        </HStack>
                        {entry.error_message && (
                          <HStack className="admin-detail-row">
                            <Text fontWeight="semibold" minW="160px">Error Message:</Text>
                            <Text>{entry.error_message}</Text>
                          </HStack>
                        )}
                        {entry.kiosk_ip && (
                          <HStack className="admin-detail-row">
                            <Text fontWeight="semibold" minW="160px">Kiosk IP:</Text>
                            <Text>{entry.kiosk_ip}</Text>
                          </HStack>
                        )}
                        {entry.user_agent && (
                          <HStack className="admin-detail-row">
                            <Text fontWeight="semibold" minW="160px">User Agent:</Text>
                            <Text fontSize="xs" maxW="500px" isTruncated title={entry.user_agent}>
                              {entry.user_agent}
                            </Text>
                          </HStack>
                        )}
                        {entry.resolved_uid && (
                          <HStack className="admin-detail-row">
                            <Text fontWeight="semibold" minW="160px">Resolved UID:</Text>
                            <Text>{entry.resolved_uid}</Text>
                          </HStack>
                        )}
                        {entry.resolved_user_id && (
                          <HStack className="admin-detail-row">
                            <Text fontWeight="semibold" minW="160px">Resolved User ID:</Text>
                            <Text>{entry.resolved_user_id}</Text>
                          </HStack>
                        )}
                        {entry.upstream_response_time_ms != null && (
                          <HStack className="admin-detail-row">
                            <Text fontWeight="semibold" minW="160px">Upstream Latency:</Text>
                            <Text>{entry.upstream_response_time_ms}ms</Text>
                          </HStack>
                        )}
                        {entry.rate_limit_state && entry.rate_limit_state !== "ok" && (
                          <HStack className="admin-detail-row">
                            <Text fontWeight="semibold" minW="160px">Rate Limit:</Text>
                            <Badge colorScheme={entry.rate_limit_state === "exceeded" ? "red" : "yellow"}>
                              {entry.rate_limit_state}
                            </Badge>
                            {entry.rate_limit_remaining != null && (
                              <Text>({entry.rate_limit_remaining} remaining)</Text>
                            )}
                          </HStack>
                        )}
                        {entry.error_traceback && (
                          <Box>
                            <Text fontWeight="semibold" mb={1}>Traceback:</Text>
                            <Code
                              display="block"
                              whiteSpace="pre-wrap"
                              p={3}
                              borderRadius="md"
                              fontSize="xs"
                              maxH="200px"
                              overflowY="auto"
                            >
                              {entry.error_traceback}
                            </Code>
                          </Box>
                        )}
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          ID: {entry.id}
                        </Text>
                      </VStack>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </VStack>

            {/* Pagination */}
            <AdminPagination page={page} totalPages={totalPages} totalItems={total} pageSize={50} isLoading={loading} onPageChange={setPage} itemLabel="attempts" />
          </>
        )}
      </div>
    </Slider>
  );
}

FailedSwipesTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default FailedSwipesTab;
